import React, { useEffect, useState } from 'react';
import { useProject } from '../../context/ProjectContext';
import styles from './ProjectToolbar.module.css';

function ProjectToolbar({ variant = 'default' }: { variant?: 'default' | 'ribbon' }): React.ReactElement {
  const {
    projectRowId,
    projectName,
    setProjectName,
    isDirty,
    isSaving,
    isLoading,
    projectList,
    newProject,
    saveProject,
    loadProject,
    refreshProjectList,
    toasts,
    dismissToast,
  } = useProject();

  const [showLoadPicker, setShowLoadPicker] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(projectName);

  // Keep name input in sync when context changes (e.g. after load)
  useEffect(() => {
    setNameInput(projectName);
  }, [projectName]);

  const handleOpenLoad = async () => {
    await refreshProjectList();
    setShowLoadPicker(true);
  };

  const handleLoad = async (id: number) => {
    if (isDirty && !window.confirm('Load another project and discard the current unsaved changes?')) return;
    setShowLoadPicker(false);
    await loadProject(id);
  };

  const handleNew = () => {
    if (isDirty && !window.confirm('Create a new project and discard the current unsaved changes?')) return;
    newProject();
  };

  const handleNameBlur = () => {
    setEditingName(false);
    if (nameInput.trim() && nameInput.trim() !== projectName) {
      setProjectName(nameInput.trim());
    } else {
      setNameInput(projectName); // revert if empty or unchanged
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
    if (e.key === 'Escape') { setNameInput(projectName); setEditingName(false); }
  };

  return (
    <>
      {/* ── Toolbar strip ─────────────────────────────────────────── */}
      <div className={[styles.toolbar, variant === 'ribbon' ? styles.toolbarRibbon : ''].join(' ')} aria-label="Project commands">
        {/* Project name — click to rename */}
        <div className={styles.nameArea}>
          {editingName ? (
            <input
              className={styles.nameInput}
              value={nameInput}
              autoFocus
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              maxLength={200}
            />
          ) : (
            <button
              className={styles.nameButton}
              onClick={() => setEditingName(true)}
              title="Click to rename project"
            >
              {projectName}
              {isDirty && <span className={styles.dirtyDot} title="Unsaved changes" />}
            </button>
          )}
          {projectRowId !== null && (
            <span className={styles.idBadge}>#{projectRowId}</span>
          )}
        </div>

        <div className={styles.actions}>
          {/* New */}
          <button
            className={styles.btn}
            onClick={handleNew}
            disabled={isLoading || isSaving}
            title="Create a new project (current unsaved changes will be lost)"
          >
            <span className={styles.btnIcon} aria-hidden="true">＋</span> New
          </button>

          {/* Save */}
          <button
            className={[styles.btn, styles.btnPrimary].join(' ')}
            onClick={saveProject}
            disabled={isSaving || isLoading}
            title={projectRowId === null ? 'Save project to backend' : 'Update project on backend'}
          >
            <span className={styles.btnIcon} aria-hidden="true">✓</span> {isSaving ? 'Saving…' : (projectRowId === null ? 'Save' : 'Update')}
          </button>

          {/* Load */}
          <button
            className={styles.btn}
            onClick={handleOpenLoad}
            disabled={isLoading || isSaving}
            title="Load an existing project from the backend"
          >
            <span className={styles.btnIcon} aria-hidden="true">↥</span> {isLoading ? 'Loading…' : 'Load'}
          </button>
        </div>
      </div>

      {/* ── Load picker overlay ────────────────────────────────────── */}
      {showLoadPicker && (
        <div className={styles.overlay} onClick={() => setShowLoadPicker(false)}>
          <div className={styles.picker} onClick={(e) => e.stopPropagation()}>
            <div className={styles.pickerHeader}>
              <span>Load Project</span>
              <button className={styles.pickerClose} onClick={() => setShowLoadPicker(false)}>✕</button>
            </div>
            {projectList.length === 0 ? (
              <p className={styles.pickerEmpty}>No projects found on the backend.</p>
            ) : (
              <ul className={styles.pickerList}>
                {projectList.map((p) => (
                  <li key={p.id}>
                    <button
                      className={[
                        styles.pickerItem,
                        p.id === projectRowId ? styles.pickerItemActive : '',
                      ].join(' ')}
                      onClick={() => handleLoad(p.id)}
                    >
                      <span className={styles.pickerName}>{p.name}</span>
                      <span className={styles.pickerMeta}>
                        #{p.id} &middot; {new Date(p.updated_at).toLocaleString()}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ── Toast notifications ────────────────────────────────────── */}
      {toasts.length > 0 && (
        <div className={styles.toastStack}>
          {toasts.map((t) => (
            <div
              key={t.id}
              className={[styles.toast, styles[`toast_${t.kind}`]].join(' ')}
            >
              <span className={styles.toastMsg}>{t.message}</span>
              <button className={styles.toastClose} onClick={() => dismissToast(t.id)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default ProjectToolbar;
